import { CVData } from '../types/cv';
import { ensureCVIdsAndLayout } from './cvPagination';

export type CVDraft = {
    cvId: string | null;
    data: CVData;
    template: string;
    updatedAt: number;
};

// Renommage de marque FastCV -> FlashCV : on écrit désormais sous la nouvelle
// clé, mais on continue de lire l'ancienne clé en repli pour ne pas perdre les
// brouillons déjà enregistrés dans le navigateur des utilisateurs existants.
const draftKey = (userId: string | null | undefined, cvId: string | null) =>
    `flashcv_draft_${userId || 'anon'}_${cvId || 'new'}`;
const legacyDraftKey = (userId: string | null | undefined, cvId: string | null) =>
    `fastcv_draft_${userId || 'anon'}_${cvId || 'new'}`;

const lastKey = (userId: string | null | undefined) => `flashcv_last_draft_${userId || 'anon'}`;
const legacyLastKey = (userId: string | null | undefined) => `fastcv_last_draft_${userId || 'anon'}`;

function readDraftRaw(userId: string | null | undefined, cvId: string | null): string | null {
    return (
        localStorage.getItem(draftKey(userId, cvId)) ||
        localStorage.getItem(legacyDraftKey(userId, cvId))
    );
}

export function loadDraft(
    userId: string | null | undefined,
    cvId: string | null
): CVDraft | null {
    try {
        const raw = readDraftRaw(userId, cvId);
        if (!raw && !cvId) {
            const lastId = localStorage.getItem(lastKey(userId)) || localStorage.getItem(legacyLastKey(userId));
            if (lastId) {
                const last = readDraftRaw(userId, lastId === 'new' ? null : lastId);
                if (last) return JSON.parse(last) as CVDraft;
            }
            return null;
        }
        if (!raw) return null;
        const draft = JSON.parse(raw) as CVDraft;
        return {
            ...draft,
            data: ensureCVIdsAndLayout(draft.data),
        };
    } catch {
        return null;
    }
}

export function saveDraft(
    userId: string | null | undefined,
    cvId: string | null,
    data: CVData,
    template: string
): void {
    try {
        const payload: CVDraft = {
            cvId,
            data: ensureCVIdsAndLayout(data),
            template,
            updatedAt: Date.now(),
        };
        const key = draftKey(userId, cvId);
        localStorage.setItem(key, JSON.stringify(payload));
        localStorage.setItem(lastKey(userId), cvId || 'new');
        localStorage.removeItem(legacyDraftKey(userId, cvId));
        localStorage.removeItem(legacyLastKey(userId));
    } catch (e) {
        console.warn('Impossible de sauvegarder le brouillon local', e);
    }
}

export function clearDraft(userId: string | null | undefined, cvId: string | null): void {
    try {
        localStorage.removeItem(draftKey(userId, cvId));
        localStorage.removeItem(legacyDraftKey(userId, cvId));
        if (!cvId) {
            localStorage.removeItem(draftKey(userId, null));
            localStorage.removeItem(legacyDraftKey(userId, null));
        }
    } catch {
        /* ignore */
    }
}

export function moveDraftToId(
    userId: string | null | undefined,
    fromId: string | null,
    toId: string,
    data: CVData,
    template: string
): void {
    saveDraft(userId, toId, data, template);
    if (!fromId) clearDraft(userId, null);
}
